import { trackEvent } from '@fs/zion-analytics'
import { useFeatureFlag } from '@fs/zion-flags'
import { ControlHide, ControlView, NoticeQuestion } from '@fs/zion-icon'
import { setPreference, usePreference } from '@fs/zion-preferences'
import type { Person } from '@fs/zion-tree-types'
import { ListItem, useOverlay } from '@fs/zion-ui'
import React, { lazy, Suspense, useRef, useState, type ComponentType, type UIEvent } from 'react'
import { useTranslation } from 'react-i18next'
import setPersonPublishState from '../../../data-service/setPersonPublishState'
import useFocusPerson from '../../../data-service/useFocusPerson'
import useDataStatusOverlay from '../../../hooks/useDataStatusOverlay'
import usePersonVisibility from '../../../hooks/usePersonVisibility'
import preferenceKeys from '../../preference-keys'

const HidePersonDialog = lazy(() => import('./HidePersonDialog'))

// Not state: the banner and the mini header each mount their own copy of this row at the same time,
// and checking the box in one has to silence both.
let skippedThisSession = false

/** Exported for tests to reset the module-level choice between cases. */
export function resetSkippedThisSession(): void {
  skippedThisSession = false
}

type DisabledReason = 'confidential' | 'living'

const UNAVAILABLE_KEYS: Record<DisabledReason, string> = {
  confidential: 'tree-person.hide-person-unavailable.confidential.description',
  living: 'tree-person.hide-person-unavailable.living.description',
}

/**
 * Living and confidential people are already withheld from the public, so toggling visibility would
 * change nothing and the row says why instead of silently doing nothing.
 *
 * There is no `confidential` field on `Person`: a confidential person is a deceased person whose
 * profile has been made private. Hiding a person clears the same visibility flag, so `unpublished`
 * is the only thing separating the two — without that guard, every hidden person would read as
 * confidential and Show Person would be permanently disabled.
 */
function getDisabledReason(person: Person): DisabledReason | undefined {
  if (person.living) return 'living'
  if (person.unpublished !== true && !person.visibleToAllWhenUsingFamilySearchApps) return 'confidential'
  return undefined
}

type Props = {
  /** The classic Tools list renders without icons in some surfaces. */
  hideIcons?: boolean
  /** The source-centric menu keeps every copy mounted, so its test ids must be namespaced. */
  testIdPrefix?: string
}

/**
 * The single Hide/Show Person control. Both Tools menus render this and nothing else about the
 * feature, so the two lists cannot drift apart.
 */
export default function HidePersonMenuItem({ hideIcons = false, testIdPrefix }: Props): JSX.Element | null {
  const [t] = useTranslation()
  const { focusPerson } = useFocusPerson()
  const { isHidden, isPublicResearcherTree } = usePersonVisibility()
  const flagIsOn = useFeatureFlag('treeWeb_personR9_hidePerson').isOn
  const { showError, showSuccess } = useDataStatusOverlay()
  const confirmOverlay = useOverlay()
  const [isSaving, setIsSaving] = useState(false)
  const storedSkipConfirm = usePreference<boolean>(preferenceKeys.HIDE_PERSON_SKIP_CONFIRM) === true

  // The action the user chose, fixed at click. Re-deriving it at confirm time would invert it if the
  // person were hidden elsewhere while the dialog was open.
  const pendingHideRef = useRef(false)

  const personName = focusPerson?.name ?? ''

  const applyChange = async (dontShowAgain: boolean): Promise<void> => {
    if (!focusPerson) return

    const shouldHide = pendingHideRef.current
    setIsSaving(true)
    try {
      await setPersonPublishState({ personId: focusPerson.id, unpublished: shouldHide })

      // Only remember the choice once the hide or show actually succeeded, and swallow a failed
      // write so saving a preference cannot fail the action the user asked for.
      if (dontShowAgain) {
        skippedThisSession = true
        setPreference(preferenceKeys.HIDE_PERSON_SKIP_CONFIRM, true).catch(console.error)
      }

      const successKey = shouldHide
        ? 'tree-person.hide-person.success.message'
        : 'tree-person.show-person.success.message'
      showSuccess(t(successKey, { name: personName }))
      confirmOverlay.close()
    } catch (error) {
      // Leave the dialog open so the user keeps the context of what failed.
      showError(undefined, error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleClick = (event: UIEvent): void => {
    pendingHideRef.current = !isHidden

    const linkName = isHidden ? 'Tools: Show Person' : 'Tools: Hide Person'
    trackEvent({ link_name: linkName })

    if (storedSkipConfirm || skippedThisSession) {
      applyChange(false)
      return
    }
    confirmOverlay.handleClick(event)
  }

  if (!flagIsOn || !focusPerson) return null

  // Hiding is meaningless outside a public researcher tree, and readOnly is true for anyone who is
  // not a contributor or admin of that tree. Neither of them can act, so neither sees the row.
  if (!isPublicResearcherTree || focusPerson.readOnly) return null

  const Icon: ComponentType = isHidden ? ControlView : ControlHide
  const label = isHidden ? t('tree-person.show-person.label') : t('tree-person.hide-person.label')

  const disabledReason = getDisabledReason(focusPerson)
  const unavailableText = disabledReason ? t(UNAVAILABLE_KEYS[disabledReason]) : undefined

  const startElement = hideIcons ? undefined : <ListItem.Icon Icon={Icon} />
  const testId = testIdPrefix ? `${testIdPrefix}-tool-hide-person` : 'tool-hide-person'
  const endElement = unavailableText ? (
    <ListItem.IconButton data-testid={`${testId}-unavailable`} Icon={NoticeQuestion} title={unavailableText} />
  ) : undefined

  return (
    <>
      <ListItem
        data-testid={testId}
        dense
        disabled={isSaving || Boolean(disabledReason)}
        onClick={handleClick}
        primaryText={label}
        startElement={startElement}
        endElement={endElement}
      />
      <Suspense fallback="">
        {(confirmOverlay.isOpen || confirmOverlay.transitioning) && (
          <HidePersonDialog
            isSaving={isSaving}
            onConfirm={applyChange}
            overlay={confirmOverlay}
            personName={personName}
            shouldHide={pendingHideRef.current}
          />
        )}
      </Suspense>
    </>
  )
}
